import React, { useEffect } from 'react';
import { Box, Card, Typography, Avatar } from '@mui/material';
import { ALL_EVENTS_GROUP } from '../../constants';
import useSettings from '../../hooks/useSettings';
import { useUser } from '../../contexts/UserContext';
import { Child } from '../../types';

interface SidebarProps {
  eventGroups: any[];
  setSelectedEventGroup: (group: any) => void;
  selectedEventGroup: any;
  onChildChange?: (child: Child) => void;
}

// Color palette for event groups
const eventGroupColors = [
  '#2196F3', // blue
  '#4CAF50', // green  
  '#9C27B0', // purple
  '#FF9800', // orange
  '#00BCD4', // cyan
  '#E91E63', // pink
  '#3F51B5', // indigo
  '#FFC107', // amber
];

const Sidebar: React.FC<SidebarProps> = ({ eventGroups, setSelectedEventGroup, selectedEventGroup, onChildChange }) => {
  const { backgroundColor, textColor } = useSettings();
  const { user, selectedChild, setSelectedChild } = useUser();

  const allGroups = [ALL_EVENTS_GROUP, ...eventGroups];

  // Initialize selected child once (when none is selected yet).
  useEffect(() => {
    if (!user || !user.children || user.children.length === 0 || !onChildChange) return;
    if (selectedChild) return;

    const sortedChildren = [...user.children].sort(
      (a, b) => new Date(a.dob).getTime() - new Date(b.dob).getTime()
    );
    const initialChild = sortedChildren[0];
    setSelectedChild(initialChild);
    onChildChange(initialChild);
  }, [user, selectedChild, onChildChange, setSelectedChild]);

  const handleChildClick = (child: Child) => {
    setSelectedChild(child);
    if (onChildChange) {
      onChildChange(child);
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getGroupColor = (index: number, isAllEvents: boolean) => {
    if (isAllEvents) return '#757575'; // gray for "All Events"
    return eventGroupColors[(index - 1) % eventGroupColors.length];
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        mr: 2,
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* Groups: at least half of sidebar height, scrolls when many */}
      <Box
        sx={{
          flex: 1,
          minHeight: '50%', // keep at least half screen for groups when children list grows
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          pr: 2, // space between group items and scrollbar (matches staging)
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '1.125rem',
            color: '#1a1a1a',
            mb: 0.5,
            px: 0.5,
            flexShrink: 0,
          }}
        >
          Groups
        </Typography>
        {allGroups.map((group: any, index: number) => {
          const isSelected =
            !!selectedEventGroup &&
            String(selectedEventGroup.id) === String(group.id);
          const isAllEvents = group.id === ALL_EVENTS_GROUP.id;
          const groupColor = getGroupColor(index, isAllEvents);

          return (
            <Card
              key={group.id}
              onClick={() => setSelectedEventGroup(group)}
              elevation={isSelected ? 6 : 2}
              sx={{
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                backgroundColor: isSelected ? backgroundColor : '#ffffff',
                border: isSelected ? `2px solid ${backgroundColor}` : '2px solid transparent',
                flexShrink: 0,
                '&:hover': {
                  elevation: 4,
                  boxShadow: 4,
                  transform: 'translateX(4px)',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '24px',
                  }}
                >
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      backgroundColor: groupColor,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isSelected ? 700 : 600,
                    color: isSelected ? textColor : '#000',
                    fontSize: '15px',
                    lineHeight: '18px',
                    textAlign: 'left',
                  }}
                >
                  {group.name}
                </Typography>
              </Box>
            </Card>
          );
        })}
      </Box>

      {/* Children: takes remaining space (up to half), scrolls when list grows */}
      {user && user.children && user.children.length > 0 && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            overflow: 'hidden',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '1.125rem',
              color: '#1a1a1a',
              mb: 0.5,
              mt: 1,
              px: 0.5,
              flexShrink: 0,
            }}
          >
            Children
          </Typography>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              pr: 2, // space before scrollbar (matches Groups)
            }}
          >
            {[...user.children]
              .sort((a, b) => new Date(a.dob).getTime() - new Date(b.dob).getTime())
              .map((child: Child) => {
                const isSelected = selectedChild && selectedChild.id === child.id;

                return (
                  <Card
                    key={child.id}
                    onClick={() => handleChildClick(child)}
                    elevation={isSelected ? 6 : 2}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      backgroundColor: isSelected ? backgroundColor : '#ffffff',
                      border: isSelected ? `2px solid ${backgroundColor}` : '2px solid transparent',
                      flexShrink: 0,
                      '&:hover': {
                        elevation: 4,
                        boxShadow: 4,
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 1.5,
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: isSelected ? textColor : backgroundColor,
                          color: isSelected ? backgroundColor : textColor,
                          fontWeight: 600,
                          width: 32,
                          height: 32,
                          fontSize: '12px',
                        }}
                      >
                        {getInitials(child.fname, child.lname)}
                      </Avatar>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isSelected ? 700 : 600,
                          color: isSelected ? textColor : '#000',
                          fontSize: '15px',
                          lineHeight: '18px',
                          textAlign: 'left',
                        }}
                      >
                        {child.fname} {child.lname}
                      </Typography>
                    </Box>
                  </Card>
                );
              })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Sidebar;
